import React, { useState, useEffect } from 'react';
import { X, Tag } from 'lucide-react';
import { Category } from '../types';

interface CreateCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, parentId: number | null) => Promise<void>;
  loading: boolean;
  categories: Category[];
  hideWrapper?: boolean;
}

const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  categories,
  hideWrapper = false,
}) => {
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setName('');
      setParentId(null);
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      await onSubmit(name.trim(), parentId);
      onClose();
    } catch (err) {
      setError('Failed to create category. Please try again.');
    }
  };

  if (!isOpen) return null;

  // Get parent categories only (those without parent_id)
  const parentCategories = categories.filter(cat => !cat.parent_id);

  const content = (
    <>
      <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Category Name */}
            <div className="form-group">
              <label htmlFor="categoryName" className="form-label">
                Category Name *
              </label>
              <input
                id="categoryName"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError('');
                }}
                placeholder="e.g., Groceries, Transport"
                className={`form-input ${error ? 'error' : ''}`}
                disabled={loading}
                autoFocus
              />
              {error && <div className="text-error">{error}</div>}
            </div>

            {/* Parent Category (Optional) */}
            <div className="form-group">
              <label htmlFor="parentCategory" className="form-label">
                Parent Category (Optional)
              </label>
              <select
                id="parentCategory"
                value={parentId || ''}
                onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : null)}
                className="form-select"
                disabled={loading}
              >
                <option value="">None (Top-level category)</option>
                {parentCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <div className="form-help">
                Select a parent to create a subcategory
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !name.trim()}
            >
              {loading ? 'Creating...' : 'Create Category'}
            </button>
          </div>
        </form>
    </>
  );

  if (hideWrapper) {
    return content;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="expense-modal" onClick={(e) => e.stopPropagation()}>
        {content}
      </div>
    </div>
  );
};

export default CreateCategoryModal;
